// ============================================
// FICHE — STATUT, PRIORITÉ, ASSIGNÉ À (onglet Détails)
// ============================================
// Demande de Nathan (19/10/2026) : ces trois champs, jusque-là de simples
// libellés suivis d'une liste déroulante, deviennent trois cartes colorées,
// côte à côte (empilées sur téléphone) :
//   - Statut : fond pâle de la couleur du statut, son icône, son libellé en
//     gras et la phrase qui dit ce qu'il signifie ;
//   - Priorité : barre et pastille de sa couleur (Basse gris, Moyenne bleu,
//     Haute ambre, Urgente rouge), drapeau, niveau sur 4 ;
//   - Assigné à : avatar et nom du conseiller, ou « Non assigné » bien visible
//     (bordure pointillée ambre) avec « Prendre en charge » et « Auto-assigner ».
// Chaque carte garde sa liste déroulante pour changer la valeur (mêmes règles
// qu'avant). La couleur n'est jamais le seul repère : le libellé est écrit.

import React from 'react';
import { AlertTriangle, Flag, User, UserX, Zap } from 'lucide-react';
import { TicketPriority, TicketStatus } from '../../types';
import { STATUS_LABELS, PRIORITY_LABELS } from '../../shared/labels';
import { CLASSES_STATUT, COULEUR_PRIORITE, getInitials } from '../../shared/format';
import { SENS_STATUT } from '../../shared/statuts';
import { IconeStatut, couleurStatut, libelleStatut } from '../../shared/ui/BadgeStatut';
import { Button, Select } from '../../shared/ui';

const ORDRE_PRIORITES: TicketPriority[] = [TicketPriority.LOW, TicketPriority.MEDIUM, TicketPriority.HIGH, TicketPriority.URGENT];
const CHAMP = 'mt-3 bg-white/90 max-lg:min-h-11';
const TITRE = 'text-[11px] font-bold uppercase tracking-wide';

export interface ConseillerAffiche {
  id: string;
  displayName?: string | null;
  email?: string | null;
  /** Libellé du rôle (Agent, Superviseur…). */
  role?: string;
}

export const CartesGestionDossier: React.FC<{
  status: TicketStatus | string;
  priority: TicketPriority | string;
  assignee?: ConseillerAffiche;
  /** Conseillers proposés par la liste « Assigné à ». */
  optionsAffectation: Array<{ value: string; label: string }>;
  onStatut: (s: TicketStatus) => void;
  onPriorite: (p: TicketPriority) => void;
  onAffectation: (id: string | null) => void;
  onPrendreEnCharge?: () => void;
  onAutoAssigner?: () => void;
  /** Faux : « Auto-assigner » est grisé (aucun conseiller actif). */
  autoAssignerPossible?: boolean;
  occupe?: boolean;
}> = ({
  status, priority, assignee, optionsAffectation, onStatut, onPriorite, onAffectation,
  onPrendreEnCharge, onAutoAssigner, autoAssignerPossible = true, occupe = false,
}) => {
  const classesStatut = CLASSES_STATUT[status as TicketStatus] ?? 'bg-brume text-graphite border-trait';
  const couleur = couleurStatut(status);
  const sens = SENS_STATUT[status as TicketStatus];
  const p = priority as TicketPriority;
  const couleurPriorite = COULEUR_PRIORITE[p] ?? '#64748B';
  const rangPriorite = ORDRE_PRIORITES.indexOf(p) + 1;
  const IconePriorite = p === TicketPriority.URGENT ? AlertTriangle : Flag;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="cartes-gestion">
      {/* Statut */}
      <section aria-labelledby="carte-statut-titre" data-testid="carte-statut" data-statut={status}
        className={`rounded-card border border-l-4 p-3 min-w-0 ${classesStatut}`} style={{ borderLeftColor: couleur }}>
        <h3 id="carte-statut-titre" className={TITRE}>Statut</h3>
        <div className="mt-2 flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/80 flex-shrink-0" style={{ color: couleur }}>
            <IconeStatut status={status} className="w-5 h-5" />
          </span>
          <span className="text-base font-extrabold leading-tight">{libelleStatut(status)}</span>
        </div>
        {sens && <p className="mt-2 text-xs leading-snug">{sens.resume}</p>}
        <Select
          aria-label="Changer le statut"
          value={String(status)}
          disabled={occupe}
          onChange={e => onStatut(e.target.value as TicketStatus)}
          options={Object.entries(STATUS_LABELS).map(([value, label]) => ({ value, label }))}
          className={CHAMP}
        />
      </section>

      {/* Priorité */}
      <section aria-labelledby="carte-priorite-titre" data-testid="carte-priorite" data-priorite={priority}
        className="relative overflow-hidden rounded-card border border-trait bg-white p-3 pt-4 min-w-0">
        <span aria-hidden="true" className="absolute inset-x-0 top-0 h-1.5" style={{ backgroundColor: couleurPriorite }} />
        <h3 id="carte-priorite-titre" className={`${TITRE} text-graphite`}>Priorité</h3>
        <div className="mt-2 flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-full flex-shrink-0" style={{ backgroundColor: `${couleurPriorite}1A`, color: couleurPriorite }}>
            <IconePriorite className="w-5 h-5" aria-hidden="true" />
          </span>
          <span className="text-base font-extrabold leading-tight text-encre">{PRIORITY_LABELS[p] ?? String(priority)}</span>
        </div>
        {rangPriorite > 0 && (
          <div className="mt-2 flex items-center gap-1" aria-label={`Niveau ${rangPriorite} sur 4`} role="img">
            {ORDRE_PRIORITES.map((x, i) => (
              <span key={x} className="h-1.5 flex-1 rounded-full" style={{ backgroundColor: i < rangPriorite ? couleurPriorite : '#DCE4EF' }} />
            ))}
          </div>
        )}
        <Select
          aria-label="Changer la priorité"
          value={String(priority)}
          disabled={occupe}
          onChange={e => onPriorite(e.target.value as TicketPriority)}
          options={Object.entries(PRIORITY_LABELS).map(([value, label]) => ({ value, label }))}
          className={`${CHAMP} border-trait`}
        />
      </section>

      {/* Assigné à */}
      {assignee ? (
        <section aria-labelledby="carte-affectation-titre" data-testid="carte-affectation" data-assigne="oui"
          className="rounded-card border border-trait bg-white p-3 min-w-0">
          <h3 id="carte-affectation-titre" className={`${TITRE} text-graphite`}>Assigné à</h3>
          <div className="mt-2 flex items-center gap-2 min-w-0">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-petrol-600 text-white text-xs font-bold flex-shrink-0" aria-hidden="true">
              {getInitials(assignee.displayName, assignee.email)}
            </span>
            <span className="min-w-0">
              <span className="block text-base font-extrabold leading-tight text-encre truncate">{assignee.displayName || assignee.email}</span>
              {assignee.role && <span className="block text-xs text-graphite">{assignee.role}</span>}
            </span>
          </div>
          <Select
            aria-label="Changer le conseiller"
            value={assignee.id}
            disabled={occupe}
            onChange={e => onAffectation(e.target.value || null)}
            options={optionsAffectation}
            placeholder="Non assigné"
            className={CHAMP}
          />
        </section>
      ) : (
        <section aria-labelledby="carte-affectation-titre" data-testid="carte-affectation" data-assigne="non"
          className="rounded-card border-2 border-dashed border-[#D99A2B] bg-[#FBF1E1] p-3 min-w-0 text-[#7A4A00]">
          <h3 id="carte-affectation-titre" className={TITRE}>Assigné à</h3>
          <div className="mt-2 flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/80 flex-shrink-0">
              <UserX className="w-5 h-5" aria-hidden="true" />
            </span>
            <span className="text-base font-extrabold leading-tight">Non assigné</span>
          </div>
          <p className="mt-1 text-xs">Personne ne suit ce dossier.</p>
          {(onPrendreEnCharge || onAutoAssigner) && (
            <div className="mt-2 flex flex-wrap gap-2">
              {onPrendreEnCharge && (
                <Button variant="primary" size="sm" onClick={onPrendreEnCharge} disabled={occupe} className="max-lg:min-h-11">
                  <User className="w-4 h-4 mr-1" aria-hidden="true" />Prendre en charge
                </Button>
              )}
              {onAutoAssigner && (
                <Button variant="secondary" size="sm" onClick={onAutoAssigner} disabled={occupe || !autoAssignerPossible} className="max-lg:min-h-11"
                  title={autoAssignerPossible ? 'Confier le dossier au conseiller actif le moins chargé' : 'Aucun agent actif disponible'}>
                  <Zap className="w-4 h-4 mr-1 text-[#B26A00]" aria-hidden="true" />Auto-assigner
                </Button>
              )}
            </div>
          )}
          <Select
            aria-label="Changer le conseiller"
            value=""
            disabled={occupe}
            onChange={e => onAffectation(e.target.value || null)}
            options={optionsAffectation}
            placeholder="Non assigné"
            className={`${CHAMP} border-[#F0D9AE]`}
          />
        </section>
      )}
    </div>
  );
};

export default CartesGestionDossier;
